import React, { PropsWithChildren, useMemo, FC } from "react";
import {
  DynamicActionsProvider,
  ButtonGroupItemProps,
  DynamicItemsEvaluationHook,
  useAppConfigurator,
  IButtonItem,
  FormMarkup,
} from "@shesha-io/reactjs";
import { useOrganisationalAccounts } from "../hooks";
import { makeActionConfig } from "../../../utils/configurableActions";
import settingsFormJson from "./dynamic-org-account-settings.json";

export interface IOrganisationalAccountActionsProps {}

export interface IDynamicOrgAccountSettings {
  canNavigate: boolean;
  navigateUrl: string;
  organisationType: string;
}

const settingsForm = settingsFormJson as FormMarkup;

const useOrganisationalAccountActions: DynamicItemsEvaluationHook<
  IDynamicOrgAccountSettings
> = ({ item, settings }) => {
  const { data, isLoading, error } = useOrganisationalAccounts({
    organisationType: settings?.organisationType,
  });
  const { configurationItemMode } = useAppConfigurator();

  // fetch definitions. Dependencies: current user, CF mode
  const operations = useMemo<ButtonGroupItemProps[]>(() => {
    if (!data || isLoading || error) return [];

    const result = data.map<IButtonItem>((p) => ({
      id: p.id,
      name: p.name,
      label: p.name,
      tooltip: p.name,
      itemType: "item",
      itemSubType: "button",
      sortOrder: 0,
      actionConfiguration: makeActionConfig({
        actionName: "Execute Script",
        actionOwner: "shesha.common",
        actionArguments: {
          expression: `
                        contexts.webStorage.local.currentOrganisation = '${p.id}';
                        contexts.webStorage.local.currentOrganisationName = '${p.name}';
                        contexts.webStorage.local.currentOrganisationType = '${p.organisationTypeExpr}';
                        setGlobalState({ key: 'currentOrganisationName', data: '${p.name}' });
                        setGlobalState({ key: 'currentOrganisation', data: '${p.id}' });
                        setGlobalState({ key: 'currentOrganisationType', data: '${p.organisationTypeExpr}' });
                        `,
        },
        onFail: makeActionConfig({
          actionName: "Show Confirmation Dialog",
          actionOwner: "shesha.common",
          actionArguments: {
            title: "Error",
            content: "Failed to change organisation",
            okText: "",
            cancelText: "",
          },
          onSuccess: undefined,
          onFail: undefined,
        }),
        onSuccess: undefined,
      }),
    }));
    return result;
  }, [item, data, configurationItemMode, settings]);

  return operations;
};

export const OrganisationalAccountActions: FC<
  PropsWithChildren<IOrganisationalAccountActionsProps>
> = ({ children }) => {
  return (
    <DynamicActionsProvider
      id="agrierp-change-organisation"
      name="Agri:Switch Organisation"
      useEvaluator={useOrganisationalAccountActions}
      hasArguments={true}
      settingsFormMarkup={settingsForm}
    >
      {children}
    </DynamicActionsProvider>
  );
};
